import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { availableDays } from '@domain/entities/Balance';
import type { Balance } from '@domain/entities/Balance';
import type { Person } from '@domain/entities/Person';
import { PersonName, personNameMessage } from '@domain/value-objects/PersonName';
import { getServices } from '@infrastructure/services';
import type { PersonId } from '@shared/brand';
import { newestLive, periodYearFor, totalAvailable } from '../../balancePeriod';
import { useLiveBalances } from '../../hooks/useLiveBalances';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { useAccess } from '../../hooks/useAccess';
import { useSessionState } from '../../providers/SessionProvider';
import { loadFailed } from '../../loadError';
import { balanceLinesFor, type BalanceLine } from './balanceLines';
import { detailPatch, isDirty, type EditableDetail } from './detailPatch';

/**
 * Todo lo que la ficha administrativa necesita saber y hacer.
 *
 * La vista no calcula nada: recibe los números ya resueltos y unos manejadores. Es
 * la misma división que hace `useHomeViewModel`, y aquí importa más de lo normal
 * porque los números del saldo son derivados y la tentación de despejarlos en el
 * JSX —donde nadie los prueba— es real.
 */

export type { EditableDetail } from './detailPatch';
export type { BalanceLine } from './balanceLines';

const isoOf = (value: { toISO: () => string } | null): string => (value === null ? '' : value.toISO());

/**
 * El disponible, despejado igual que en el servidor.
 *
 * Se recalcula aquí en vez de leer `remainingDays` del documento porque esta
 * pantalla existe para *cambiar* dos de los sumandos, y el contador guardado
 * todavía trae los de antes. Enseñarlo sería prometer un resultado que la
 * siguiente lectura desmiente.
 */
const previewAvailable = (
  balance: Balance | null,
  openingUsedDays: number,
  entitled: number,
): number =>
  balance === null
    ? 0
    : Math.max(
        0,
        entitled -
          openingUsedDays -
          balance.takenDays -
          balance.scheduledDays -
          balance.pendingDays,
      );

export const usePersonDetailViewModel = (personId: PersonId) => {
  const { holds, holdsAny } = useAccess();
  const { status } = useSessionState();
  const config = useRuntimeConfig();
  const viewerPersonId = status.kind === 'ready' ? status.session.personId : null;
  const queryClient = useQueryClient();
  const canEdit = holds('editar_detalle');
  /*
   * Ajustar el saldo va por su cuenta, no con `editar_detalle`.
   *
   * Corregir un nombre mal escrito y otorgarle días a alguien son trabajos
   * distintos, y los gobernaba el mismo permiso. Los dos números de esta tarjeta
   * mueven cuántos días puede pedir una persona.
   */
  const canAdjustBalance = holds('ajustar_saldo');
  /*
   * Cambiar el rol va con `admin` u `areas`, no con `editar_detalle`.
   *
   * Es el mismo par que exige `setPersonRole` en el servidor, y no coincide con el
   * resto de la ficha a propósito: `editar_detalle` sólo lo tiene el superusuario,
   * así que colgarlo de ahí escondería el botón justo a quien administra su área y
   * a quien el servidor sí se lo permite. Dos comprobaciones distintas mintiendo
   * la una sobre la otra es peor que una sola bien puesta.
   */
  const canChangeRole = holdsAny(['admin', 'areas']);
  /*
   * Repartir permisos es configuración de la plataforma, no administración de un
   * área: pide `config`, que sólo tienen los roles sin frontera. Es la misma
   * frontera que ya protege a los roles sin área — quien vive dentro de un área no
   * decide quién ve los sueldos de las otras siete. El servidor lo revalida, y
   * además impide que nadie edite los suyos.
   */

  const person = useQuery({
    queryKey: ['personDetail', personId],
    queryFn: async () => {
      const result = await getServices().people.getById(personId);
      if (!result.ok) throw loadFailed('No pudimos cargar esta ficha.', result.error);
      return result.value;
    },
  });

  const hireDate = person.data?.hireDate ?? null;

  /*
   * Todos los periodos vigentes, y uno elegido.
   *
   * Con «Vencimiento del saldo» en más de 12 meses lo que queda del periodo
   * anterior sigue valiendo junto al nuevo, y cada uno se corrige por separado:
   * «ya gastados fuera del portal» es de un periodo, no de la persona. Por omisión
   * se mira el más nuevo, que es lo que esta tarjeta enseñaba antes.
   */
  const liveBalances = useLiveBalances({
    scope: 'personDetail',
    personId,
    hireDate,
    expiryMonths: config.rules.expiryMonths,
    enabled: person.data !== undefined,
  });
  const lives = useMemo(() => liveBalances.data ?? [], [liveBalances.data]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const newest = newestLive(lives);
  const selected = lives.find((live) => live.year === selectedYear) ?? newest;

  /* La misma forma que tenía la consulta de un solo periodo, para que la vista y
     las acciones de al lado no cambien: `data` es el saldo del periodo elegido. */
  const balance = {
    data: liveBalances.isLoading ? undefined : (selected?.balance ?? null),
    isLoading: liveBalances.isLoading,
    refetch: liveBalances.refetch,
  };

  const subArea = useQuery({
    queryKey: ['personDetail', 'subArea', person.data?.subAreaId ?? null],
    enabled: (person.data?.subAreaId ?? null) !== null,
    queryFn: async () => {
      const id = person.data?.subAreaId ?? null;
      if (id === null) return null;
      const result = await getServices().organization.getSubArea(id);
      return result.ok ? result.value : null;
    },
  });

  /** Quién le firma. Es lo primero que se pregunta al abrir la ficha de alguien. */
  /*
   * Los líderes, y **cuántos vínculos hay aunque sus nombres no resuelvan**.
   *
   * Antes esto devolvía una lista de personas y nada más, así que un fallo al
   * resolver los nombres —un permiso denegado, un id que ya no existe— salía como
   * lista vacía, indistinguible de «no tiene líderes». La ficha entonces afirmaba
   * «No tiene líderes asignados, así que sus solicitudes no llegan a nadie» sobre
   * alguien que sí los tenía: una frase falsa y alarmante, sobre lo único que
   * decide quién firma sus vacaciones.
   *
   * El liderazgo se asigna **por persona** en `personLeaders`, nunca por sub área,
   * así que tener o no responsable de sub área no cambia nada aquí. Es la misma
   * confusión que ya costó el tablero de aprobaciones filtrando por sub área.
   *
   * Ahora se devuelven las dos cosas y la pantalla decide: sin vínculos, el aviso;
   * con vínculos y sin nombres, se dice que no se pudieron cargar.
   */
  /*
   * El área de la persona de la ficha, no la de quien mira.
   *
   * La consulta la necesita para que la regla se pueda probar —ver
   * `IPersonLeaderRepository.leadersOf`— y sale del documento de la persona, así
   * que la consulta espera a que ese cargue en vez de disparar con `undefined`.
   */
  const personAreaId = person.data?.areaId;

  const leaders = useQuery({
    queryKey: ['personDetail', 'leaders', personId, personAreaId],
    enabled: personAreaId !== undefined,
    queryFn: async () => {
      if (personAreaId === undefined) {
        return { failed: true, linkCount: 0, people: [] as readonly Person[] };
      }
      const links = await getServices().leaders.leadersOf(personId, personAreaId);
      /*
       * Cuatro estados y no tres. El que faltaba es éste: la consulta falló.
       *
       * Se contaba como `linkCount: 0`, y con eso la ficha decía «No tiene líderes
       * asignados, así que sus solicitudes no llegan a nadie» — una afirmación
       * sobre la organización, hecha a partir de no haber podido preguntar. Es lo
       * que QA reportó el 18 de agosto en los navegadores donde el canal de
       * escucha no conecta.
       */
      if (!links.ok) {
        return { failed: true, linkCount: 0, people: [] as readonly Person[] };
      }
      const linkCount = links.value.length;
      if (linkCount === 0) {
        return { failed: false, linkCount, people: [] as readonly Person[] };
      }
      const people = await getServices().people.getMany(links.value.map((l) => l.leaderPersonId));
      return {
        failed: false,
        linkCount,
        people: people.ok ? people.value : ([] as readonly Person[]),
      };
    },
  });

  const [draft, setDraft] = useState<EditableDetail | null>(null);
  const [openingDraft, setOpeningDraft] = useState<string | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<string | null>(null);

  const saved: EditableDetail = useMemo(
    () => ({
      displayName: person.data?.displayName ?? '',
      email: String(person.data?.email ?? ''),
      hireDate: isoOf(person.data?.hireDate ?? null),
      birthDate: isoOf(person.data?.birthDate ?? null),
      shirtSize: person.data?.shirtSize ?? '',
      parentRole: person.data?.parentRole ?? '',
      positionId: person.data?.positionId ?? '',
    }),
    [person.data],
  );

  const current = draft ?? saved;

  /** El mismo mensaje que ve alguien corrigiendo su propio nombre en el perfil. */
  const nameError = useMemo(() => {
    if (draft === null || current.displayName === saved.displayName) return null;
    const parsed = PersonName.create(current.displayName);
    return parsed.ok ? null : personNameMessage(parsed.error);
  }, [draft, current.displayName, saved.displayName]);

  const dirty = draft !== null && isDirty(saved, current);

  const detailMutation = useMutation({
    mutationFn: async () => {
      const result = await getServices().people.updateDetail(
        personId,
        detailPatch(saved, current),
      );
      if (!result.ok) throw loadFailed('No se pudo guardar la corrección.', result.error);
      return result.value;
    },
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['personDetail'] });
    },
  });

  const entitled = person.data?.entitledDaysOverride ?? balance.data?.entitledDays ?? 0;
  const openingSaved = balance.data?.openingUsedDays ?? 0;
  const openingValue = openingDraft === null ? openingSaved : Number(openingDraft);
  const overrideSaved = person.data?.entitledDaysOverride ?? null;
  const overrideValue =
    overrideDraft === null || overrideDraft === '' ? null : Number(overrideDraft);

  const entitledPreview = overrideDraft === null ? entitled : (overrideValue ?? balance.data?.entitledDays ?? 0);

  const balanceMutation = useMutation({
    mutationFn: async () => {
      const adjustment: Record<string, unknown> = {};
      if (openingDraft !== null && Number(openingDraft) !== openingSaved) {
        adjustment['openingUsedDays'] = Number(openingDraft);
      }
      if (overrideDraft !== null && overrideValue !== overrideSaved) {
        adjustment['entitledDaysOverride'] = overrideValue;
      }

      if (selected !== null) adjustment['periodYear'] = selected.year;

      const result = await getServices().people.adjustBalance(personId, adjustment);
      if (!result.ok) throw loadFailed('No se pudo ajustar el saldo.', result.error);
      return result.value;
    },
    onSuccess: async () => {
      setOpeningDraft(null);
      setOverrideDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['personDetail'] });
    },
  });

  /*
   * Repartir permisos es configuración de la plataforma —`config`, que sólo
   * tienen los roles sin frontera— y **nunca sobre uno mismo**: quien puede
   * ampliarse a sí mismo no tiene techo, y la bitácora deja de significar nada
   * cuando el autor y el beneficiario coinciden. El servidor lo rechaza igual;
   * esto es para no ofrecer un botón que va a fallar.
   */
  const canGrantPermissions = holds('config') && personId !== viewerPersonId;

  const lines: readonly BalanceLine[] = useMemo(() => {
    const b = balance.data;
    if (b === undefined || b === null) return [];
    return balanceLinesFor({ balance: b, entitled: entitledPreview, opening: openingValue });
  }, [balance.data, entitledPreview, openingValue]);

  return {
    canEdit,
    canChangeRole,
    canGrantPermissions,
    canAdjustBalance,
    person,
    balance,
    /** El periodo que se está mirando, exista o no su registro. */
    periodYear: selected?.year ?? periodYearFor(hireDate, getServices().clock.today()),
    /** Los periodos vigentes, para elegir cuál ajustar. */
    periods: lives.map((live) => ({
      year: live.year,
      available: live.available,
      lastValidDay: live.lastValidDay,
      selected: live === selected,
    })),
    selectPeriod: (year: number) => {
      setSelectedYear(year);
      setOpeningDraft(null);
      setOverrideDraft(null);
    },
    /** Lo que se le deja pedir hoy entre todos sus periodos: lo que liquida una baja. */
    availableTotal: totalAvailable(lives),
    selectedLastValidDay: selected?.lastValidDay ?? null,
    /** El derecho por contrato es de la persona y rige desde su periodo nuevo. */
    isNewestPeriod: selected === newest,
    /**
     * Si el saldo de ese periodo todavía no existe.
     *
     * Distinto de «tiene cero días», y la diferencia importa: corregir la fecha
     * de ingreso mueve el aniversario, así que la pantalla empieza a pedir un
     * periodo nuevo — y sin registro pintaba ceros, que se lee como si al
     * corregir un dato se le hubieran borrado los días a alguien. No se borra
     * nada: el saldo del periodo anterior sigue guardado.
     */
    balanceMissing: !balance.isLoading && (balance.data === null || balance.data === undefined),
    subArea,
    leaders,
    current,
    saved,
    dirty,
    nameError,
    setDraft: (patch: Partial<EditableDetail>) => {
      setDraft((previous) => ({ ...(previous ?? saved), ...patch }));
    },
    discard: () => {
      setDraft(null);
    },
    detailMutation,
    lines,
    openingDraft,
    setOpeningDraft,
    overrideDraft,
    setOverrideDraft,
    openingSaved,
    /** La parte de lo descontado al abrir que es deuda del periodo anterior. */
    carriedDebt: balance.data?.carriedDebtDays ?? 0,
    overrideSaved,
    balanceDirty:
      (openingDraft !== null && Number(openingDraft) !== openingSaved) ||
      (overrideDraft !== null && overrideValue !== overrideSaved),
    balanceMutation,
    /** El disponible que resultaría de guardar lo que hay escrito ahora mismo. */
    availablePreview: previewAvailable(balance.data ?? null, openingValue, entitledPreview),
    availableSaved: balance.data === null || balance.data === undefined ? 0 : availableDays(balance.data),
  };
};
